import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchCustomerCategory, fetchCustomerType, addCustomerCategoryCriteriaValue } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function CustomerCategoryClassificationCriteriaAddPage() {
  const { businessDomainId, businessUnitId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [criterias, setCriterias] = useState([]);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [allValues, setAllValues] = useState([]);

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/customer-categories/${categoryId}`;

  useEffect(() => {
    (async () => {
      try {
        const cat = await fetchCustomerCategory(categoryId);
        const ct = await fetchCustomerType(cat.customerTypeId);
        // Use classificationCriterias directly (all defined criteria for this customer type)
        setCriterias(ct.classificationCriterias || []);
        setAllValues(ct.classificationCriteriaValues || []);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleCriteriaChange = (e) => {
    const name = e.target.value;
    setSelectedCriteria(name);
    setSelectedValue("");
    const filtered = allValues.filter(
      (v) => v.classificationCriteria?.classificationCriteriaName === name
    );
    setCriteriaValues(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCriteria || !selectedValue) {
      notify.error(t("customerCategory.classificationCriteria.selectRequired"));
      return;
    }
    try {
      const value = criteriaValues.find(
        (v) => v.classificationCriteriaValueName === selectedValue
      );
      if (!value) return;
      const res = await addCustomerCategoryCriteriaValue(categoryId, value);
      if (res?.result === "NG") {
        notify.error(res.errorMessage || t("message.create.error"));
        return;
      }
      notify.success(t("message.create.success"));
      navigate(`${basePath}/classification-criteria`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("customerCategory.classificationCriteriaValues")}：{t("common.add")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>

        {/* 分類基準 */}
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("customerCategory.classificationCriteriaName")}</InputLabel>
          <Select
            value={selectedCriteria}
            onChange={handleCriteriaChange}
            label={t("customerCategory.classificationCriteriaName")}
          >
            {criterias.map((c) => (
              <MenuItem key={c.classificationCriteriaName} value={c.classificationCriteriaName}>
                {c.classificationCriteriaName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 分類基準値 */}
        <FormControl fullWidth required sx={{ mb: 3 }} disabled={!selectedCriteria}>
          <InputLabel>{t("customerCategory.classificationCriteriaValueName")}</InputLabel>
          <Select
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            label={t("customerCategory.classificationCriteriaValueName")}
          >
            {criteriaValues.map((v) => (
              <MenuItem key={v.classificationCriteriaValueName} value={v.classificationCriteriaValueName}>
                {v.classificationCriteriaValueName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/classification-criteria`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
